import GlobalNetworkMap from "@components/GlobalNetworkMap/GlobalNetworkMap"
import { Head as BaseHead } from "@components/Head/Head"
import { MikanSpan } from "@components/Mikan"
import {
  Body,
  Footer,
  Header,
  StyledSuggestContent,
  StyledSuggestContentIntro,
  StyledSuggestContentItem,
  StyledSuggestContentList,
} from "@components/ProductSuggestContent/SuggestContent"
import ResourceIcon from "@components/ResourceIcon/ResourceIcon"
import StyledHero from "@components/StyledHero"
import SupportPlans from "@components/SupportPlans"
import RichText from "@components/rich-text/renderer"
import { Box, Button as ConsistentlyButton, Container, OnThisPage, hooks } from "@fastly/consistently"
import { Eyebrow, H1, H6, PreFooter, Text } from "@fastly/consistently-vanilla"
import { NetworkMapFootNotes } from "@templates/pages/styled2021"
import { findById } from "@utils/contentful"
import { graphql } from "gatsby"
import slugify from "slugify"
import styled, { css } from "styled-components"
import { links as links_cn, preFooterWrapper, text as text_cn } from "../components/PreFooter/PreFooter.module.css"

const tokens = css`
  --max-width: 48.5rem; // 776px
  --down-scale-h2: var(--h3);
  --down-scale-h3: var(--h4);
  --down-scale-h4: var(--h5);
  --down-scale-h5: var(--h6);
  --down-scale-h6: var(--text-md);
`

const MainWebResources = styled.main`
  p + img,
  h5 + img {
    margin-top: var(--size-space-xl);
    margin-bottom: var(--size-space-sm);
  }

  img + h6,
  img + p {
    margin-top: none;
    margin-bottom: var(--size-space-xl);
  }
`
const ResourcesRTWrapper = styled(Container).attrs({
  overflow: "inherit",
})``

const ResourcesRTBody = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: 1fr 5fr;
    grid-gap: 20px;
  }
`

const ResourcesRTContent = styled(Box)`
  ${tokens}
  max-width: var(--max-width);
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    margin: 0;
  }

  h2 {
    font: var(--down-scale-h2);
    margin-top: var(--size-space-3xl);
    margin-bottom: var(--size-space-md);
  }

  h2:first-of-type {
    border-top: none;
    margin-top: 0;
  }

  h3 {
    font: var(--down-scale-h3);
    margin-top: var(--size-space-3xl);
    margin-bottom: var(--size-space-md);
  }

  h4 {
    font: var(--down-scale-h4);
    margin-top: var(--size-space-3xl);
    margin-bottom: var(--size-space-md);
  }

  h5 {
    font: var(--down-scale-h5);
    margin-top: var(--size-space-2xl);
    margin-bottom: var(--size-space-md);
  }

  h6 {
    font: var(--down-scale-h6);
    margin-top: 1rem;
    margin-bottom: var(--size-space-md);
  }

  //override default browser hr styles
  hr {
    border-color: var(--color-grey-30);
    border-style: solid;
    margin-top: 0;
    // override margin-bottom from common.css
    margin-bottom: 0 !important;
    height: 1px;
    border-bottom: 1px;
  }

  // clear out any p or br inserted by Contentful
  // handle spacing based on header/component
  br {
    display: none;
  }

  p:empty {
    display: none;
  }

  table {
    margin-top: var(--size-space-2xl);
    margin-bottom: var(--size-space-2xl);
  }
  th:not(th:first-of-type),
  td:not(td:first-of-type) {
    text-align: center;
  }
`

const OnThisPageResources = styled(OnThisPage)`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: block;
    padding-top: 0.5rem;
    padding-right: 40px;
    z-index: 1;

    h3 {
      font-size: var(--text-md);
      margin-bottom: 1.3rem;
    }

    a {
      color: var(--color-grey-80);
    }
  }
`

const Hero = styled(StyledHero)`
  ${tokens}
  text-align: left;
  background-color: var(--color-grey-90);
  color: var(--color-white);
  position: relative;
  padding-block: ${({ theme }) => theme.space["4xl"]};

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    &::before {
      content: " ";
      background: url("/g-static/nimbus/web-resources/hero-nimbus.svg") no-repeat;
      position: absolute;
      top: -155px;
      right: -200px;
      width: 500px;
      height: 500px;
    }

    &::after {
      content: " ";
      background: url("/g-static/nimbus/web-resources/hero-nimbus.svg") no-repeat;
      background-size: contain;
      position: absolute;
      left: -329px;
      bottom: -134px;
      width: 500px;
      height: 500px;
    }
  }

  @media (min-width: 1920px) {
    &::before {
      top: -100px;
      right: -100px;
    }

    &::after {
      left: -90px;
      width: 500px;
    }
  }

  & header {
    max-width: var(--max-width);

    > div {
      padding-bottom: var(--size-space-sm);
    }

    > h1 {
      background-image: conic-gradient(
        from -24.12deg at 53.64% 907.64%,
        #7af7d1 -9.38deg,
        #04b2e1 33.75deg,
        #04b2e1 60deg,
        #7af7d1 350.63deg,
        #04b2e1 393.75deg
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    section {
      margin: 0 0 var(--size-space-2xl) 0;

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        margin: 0 0 var(--size-space-4xl) 0;
      }
      div {
        margin: 0;
        nav {
          padding: 0;

          ul > li:last-of-type {
            background-image: conic-gradient(
              from -24.12deg at 53.64% 907.64%,
              #7af7d1 -9.38deg,
              #04b2e1 33.75deg,
              #04b2e1 60deg,
              #7af7d1 350.63deg,
              #04b2e1 393.75deg
            );
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
        }
      }
    }
  }
`

const HeroTopicTagWrapper = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.space.xs};
    padding-block: ${theme.space.md} ${theme.space.lg};
    margin-block: 0 ${theme.space.md};
    border-bottom: 1px solid var(--color-grey-50);
  `}
`

const HeroTopicTag = styled.span`
  padding: ${({ theme }) => theme.space[1]};
  border: 1px solid var(--color-grey-40);
  border-radius: ${({ theme }) => theme.radii.sm};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: inherit;
  }
`

const generateOnThisPageContentData = (acc, richText) => {
  if (!richText?.raw) return

  return JSON.parse(richText.raw)
    .content.filter(
      (node) =>
        node.nodeType === "heading-2" || node.nodeType === "heading-3" || node.nodeType === "embedded-entry-block"
    )
    .reduce((array, node) => {
      // Add headings from embedded spotlight section
      if (node.nodeType === "embedded-entry-block") {
        const referenceNode = richText.references.find((ref) => ref.contentful_id === node.data.target.sys.id)
        if (referenceNode?.__typename === "ContentfulWebBasedResourcesSpotlight") {
          return generateOnThisPageContentData(array, referenceNode.spotlight)
        }
        return array
      }

      const title = node.content[0].value
      const link = slugify(title, { lower: true, remove: /[.:,?]/g }).replace(/'/g, "-")

      if (node.nodeType === "heading-2") {
        return [...array, { title: title, url: `#${link}` }]
      } else if (node.nodeType === "heading-3") {
        // empty object prevents errors if h3 precedes any h2
        const previousNode = array[array.length - 1] ?? {}

        // Add h3 to the items array on the previous h2 (i.e. nest h3 items)
        previousNode.items = previousNode.items
          ? [...previousNode.items, { title: title, url: `#${link}` }]
          : [{ title: title, url: `#${link}` }]
      }
      return array
    }, acc)
}

const ResourcesTemplate = ({ data }) => {
  const page = data.resources
  const { title, eyebrow, tags, summary, body, globalNetwork, supportPlans, relatedResources, hidePrefooter } = page
  const { useIntersectionObserver } = hooks
  const { containerRef, activeEl } = useIntersectionObserver({
    root: null,
    rootMargin: "-5% 0% -88% 0%",
    threshold: 0.5,
  })

  const networkMap = data.networkMap.section_content
  const networkMapText = findById(networkMap, "6YY09jxSQ6QUpQiuSX7xwS")
  const networkMapValues = findById(networkMap, "77iHn6bwLVs7ySMaUJXD03").section_content
  const networkMapCta = findById(networkMap, "61Vx2j1V4mmQXZiQtYOFE7")
  const { networkMapTitle, networkMapFootnotes, contentsTitle } = data
  const relatedResourcesTitle = data.relatedResourcesTitle

  const showSupportPlans = supportPlans
  const supportPlanContent = data.supportPlansContentGroup.section_content.map((text) => (
    <RichText key={text.contentful_id} body={text.rtContent} />
  ))

  const onThisPageContent = generateOnThisPageContentData([], body)

  const preFooter = data.preFooter.section_content
  const preFooterText = preFooter[0]
  const preFooterCta = preFooter[1]

  if (relatedResources) {
    onThisPageContent.push({ title: relatedResourcesTitle.text, url: "#related-resources" })
  }

  return (
    <MainWebResources>
      {/* Hero */}
      <Container fullWidth={true}>
        <Hero>
          {eyebrow && (
            <Eyebrow as="div">
              <MikanSpan text={eyebrow} />
            </Eyebrow>
          )}
          <H1>
            <MikanSpan text={title} />
          </H1>
          <HeroTopicTagWrapper>
            {tags.map((tag) => (
              <HeroTopicTag key={tag.title}>
                <MikanSpan text={tag.title} />
              </HeroTopicTag>
            ))}
          </HeroTopicTagWrapper>
          <Text as="p" variant="xl" alignText="left">
            <MikanSpan text={summary?.internal.content} />
          </Text>
        </Hero>
      </Container>

      {/* Body */}
      <ResourcesRTWrapper py="2xl">
        <ResourcesRTBody>
          <OnThisPageResources
            offsetTop="80px"
            label={contentsTitle.text}
            navContent={onThisPageContent}
            activeEl={activeEl}
          />

          <ResourcesRTContent ref={containerRef}>
            <RichText body={body}></RichText>
          </ResourcesRTContent>
        </ResourcesRTBody>
      </ResourcesRTWrapper>

      {/* Resources */}
      {relatedResources ? (
        <Container background="var(--color-grey-10)" py="0">
          <StyledSuggestContent>
            <StyledSuggestContentIntro>
              <h2 id="related-resources">
                <MikanSpan text={relatedResourcesTitle.text} />
              </h2>
            </StyledSuggestContentIntro>
            <StyledSuggestContentList>
              {relatedResources.map((item) => {
                return (
                  <StyledSuggestContentItem key={item.contentful_id}>
                    <Header>
                      <ResourceIcon type={item.type} color="var(--color-blue-80)" width="24" height="24" />
                      <Box ml="0.8rem">{item.type}</Box>
                    </Header>
                    <Body>
                      <H6 as="div" mb="xs">
                        {item.title}
                      </H6>
                      {item.description ? <p>{item.description} </p> : null}
                    </Body>
                    <Footer>
                      <ConsistentlyButton variant="link" icon="arrow" url={item.linkUrl}>
                        {item.linkText}
                      </ConsistentlyButton>
                    </Footer>
                  </StyledSuggestContentItem>
                )
              })}
            </StyledSuggestContentList>
          </StyledSuggestContent>
        </Container>
      ) : null}

      {/* Network map */}
      {globalNetwork ? (
        <>
          <GlobalNetworkMap
            data={networkMap}
            margBtm={{ sm: "64px" }}
            margTop={{ sm: "64px" }}
            padding={{ sm: "0 1rem", md: "1rem" }}
            size={{ minHeight: { sm: "555px", md: "558px" } }}
            title={networkMapTitle.text}
            text={networkMapText}
            values={networkMapValues}
            cta={networkMapCta}
          />
          <NetworkMapFootNotes>
            {networkMapFootnotes.section_content.map((item) => (
              <RichText body={item.rtContent} key={item.contentful_id} />
            ))}
          </NetworkMapFootNotes>
        </>
      ) : null}

      {/* Support Plan */}
      {showSupportPlans ? (
        <Container background="var(--color-grey-80)" py="3xl">
          <SupportPlans content={supportPlanContent} />
        </Container>
      ) : null}

      {/* Pre-footer */}
      {!hidePrefooter && preFooterText ? (
        <div className={preFooterWrapper}>
          <PreFooter id="preFooter">
            <div className={text_cn}>
              <RichText body={preFooterText.rtContent} mikan={preFooterText.mikan} />
            </div>
            <div className={links_cn}>
              <RichText body={preFooterCta.rtContent} mikan={preFooterCta.mikan} />
            </div>
          </PreFooter>
        </div>
      ) : null}
    </MainWebResources>
  )
}

export default ResourcesTemplate

export const Head = (props) => {
  const { title, metaTitle, metaImage, metaDescription, noIndex } = props.data.resources

  props.data.page = {}
  props.data.page.socialTitle = metaTitle ? metaTitle : title.title
  props.data.page.socialImage = metaImage
  props.data.page.socialDescription = metaDescription

  return <BaseHead {...props}>{noIndex ? <meta name="robots" content="noindex" /> : null}</BaseHead>
}

export const query = graphql`
  query ($slug: String!) {
    resources: contentfulWebBasedResources(slug: { eq: $slug }) {
      contentful_id
      id
      slug
      metaTitle
      metaDescription
      metaImage {
        file {
          url
        }
      }
      eyebrow
      title
      tags {
        title
      }
      summary {
        internal {
          content
        }
      }
      body {
        raw
        references {
          ...spotlight
          ...keyStat
          ...customerQuote
          ...twoColumnLayout
          ...asset
          ...analystCallOut
        }
      }
      relatedResources {
        contentful_id
        title
        type
        description
        linkUrl
        linkText
      }
      supportPlans
      globalNetwork
      hidePrefooter
      noIndex
    }
    contentsTitle: contentfulMicrocopy(contentful_id: { eq: "5y6hEFLXNDDbQNYsF3qDhm" }) {
      text
    }
    networkMap: contentfulContentGroup(contentful_id: { eq: "4GK7eEs7Yd7mkONKIEpZFe" }) {
      section_content {
        ...contentGroup
        ...text
        ...image
        ...button
      }
    }
    preFooter: contentfulContentGroup(contentful_id: { eq: "13ayItU4Kx9I20qdtr0xiv" }) {
      section_content {
        ...text
      }
    }
    networkMapTitle: contentfulMicrocopy(title: { eq: "home2021:globalNetworkIntro" }) {
      text
    }
    networkMapFootnotes: contentfulContentGroup(contentful_id: { eq: "3omiKWMCxxdqWW3isLYyls" }) {
      section_content {
        ...text
      }
    }
    supportPlansContentGroup: contentfulContentGroup(contentful_id: { eq: "3aWu7gqVwFubDnPTQyn1Jk" }) {
      section_content {
        ...text
      }
    }
    relatedResourcesTitle: contentfulMicrocopy(contentful_id: { eq: "70gV9bDrHSX6gxHlUGsiZ9" }) {
      text
    }
  }
`
